import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from '@emotion/styled'
import { FaPlus, FaTrash } from 'react-icons/fa'
import { colors } from '../../../../../constants'
import { FlexContainer, Panel, XSpacing, YSpacing } from '@components/common'
import {
  Button,
  Input,
  MultiSelectDropDown,
  AutocompleteInput,
  Checkbox,
} from '@components/common/form'
import { Option, MenuTemplate, DateMenuTemplate } from '@types'
import {
  CreateDateMenuTemplateReq,
  UpdateDateMenuTemplateReq,
} from '@coordinators/groupOrders/dateMenuTemplates'
import moment from 'moment'

interface Chef {
  id: string
  name: string
}

// Custom radio button implementation
interface RadioOption {
  value: string
  label: string
}

interface RadioGroupProps {
  name: string
  options: RadioOption[]
  selectedValue: string
  onChange: (value: string) => void
}

const RadioGroup: React.FC<RadioGroupProps> = ({
  name,
  options,
  selectedValue,
  onChange,
}) => {
  return (
    <RadioGroupContainer>
      {options.map((option) => (
        <RadioOption key={option.value}>
          <input
            type="radio"
            id={`${name}-${option.value}`}
            name={name}
            value={option.value}
            checked={selectedValue === option.value}
            onChange={() => onChange(option.value)}
          />
          <label htmlFor={`${name}-${option.value}`}>{option.label}</label>
        </RadioOption>
      ))}
    </RadioGroupContainer>
  )
}

const RadioGroupContainer = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 10px;
`

const RadioOption = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`

interface MenuTemplateBuilderProps {
  hqId: number
  userId: string
  userEmail: string
  cuisineOptions: Option[]
  loadChefs: (params: any) => Promise<Chef[]>
  onSelectTemplate?: (template: DateMenuTemplate) => void
  handleTemplateUpdate: (template: DateMenuTemplate) => void
  displayFailureMessage: (message: string) => void
  displayInfoMessage: (message: string) => void
  displayConfirmationModal: ({
    title,
    text,
  }: {
    title: string
    text: string
  }) => Promise<boolean>
  handleDeleteTemplate: (template: DateMenuTemplate) => Promise<void>
  createDateMenuTemplate: (
    req: CreateDateMenuTemplateReq,
  ) => Promise<DateMenuTemplate | null>
  updateDateMenuTemplate: (
    req: UpdateDateMenuTemplateReq,
  ) => Promise<DateMenuTemplate | null>
  initialTemplate?: DateMenuTemplate
}

const buildDefaultTemplate = (
  userId: string,
  userEmail: string,
): DateMenuTemplate => ({
  id: '',
  createdAt: moment(),
  updatedAt: moment(),
  createdByUserId: userId,
  createdByEmail: userEmail,
  updatedByUserId: userId,
  updatedByEmail: userEmail,
  hqId: undefined,
  isPrivate: false,
  name: 'New Template',
  templates: [
    {
      chefIds: [],
      excludeChefIds: [],
      cuisines: [],
      excludeCuisines: [],
      maxNumReuses: 1,
    },
  ],
})

const copyTemplate = (
  template: DateMenuTemplate,
  userId: string,
  userEmail: string,
): DateMenuTemplate => ({
  ...template,
  id: '',
  createdAt: moment(),
  updatedAt: moment(),
  createdByUserId: userId,
  createdByEmail: userEmail,
  updatedByUserId: userId,
  updatedByEmail: userEmail,
  name: `${template.name} (Copy)`,
})

const MenuTemplateBuilder: React.FC<MenuTemplateBuilderProps> = ({
  hqId,
  userId,
  userEmail,
  cuisineOptions,
  loadChefs,
  onSelectTemplate,
  handleTemplateUpdate,
  displayFailureMessage,
  displayInfoMessage,
  handleDeleteTemplate,
  createDateMenuTemplate,
  updateDateMenuTemplate,
  initialTemplate,
}) => {
  const [draftDateMenuTemplate, setDraftDateMenuTemplate] = useState<
    DateMenuTemplate | undefined
  >(initialTemplate || buildDefaultTemplate(userId, userEmail))
  const [activeMenuTemplateIndex, setActiveMenuTemplateIndex] = useState(0)
  const [chefsMap, setChefsMap] = useState<Map<string, Chef>>(new Map())

  // Add state for import/export functionality
  const [showImportExport, setShowImportExport] = useState(false)
  const [importText, setImportText] = useState('')
  const [importError, setImportError] = useState<string | null>(null)

  // Add state for scope selection (Global/Market)
  const [templateScope, setTemplateScope] = useState<'global' | 'market'>(
    'global',
  )

  // Add state for tracking if we're currently saving to API
  const [isSaving, setIsSaving] = useState(false)

  const templateHqId = draftDateMenuTemplate?.hqId
  const isNewTemplate = !draftDateMenuTemplate?.id

  const dateMenuTemplateChefsOptions = useMemo(() => {
    if (!draftDateMenuTemplate) {
      return []
    }

    return draftDateMenuTemplate.templates
      .map((menuTemplate, menuIdx) => [
        {
          value: menuTemplate.chefIds?.join(','),
          label: `Menu ${menuIdx + 1} Include Chefs`,
        },
        {
          value: menuTemplate.excludeChefIds?.join(','),
          label: `Menu ${menuIdx + 1} Exclude Chefs`,
        },
      ])
      .flat()
      .filter((opt) => opt.value !== undefined && opt.value !== '')
  }, [draftDateMenuTemplate])

  const onLoadDateMenuTemplateChefs = useCallback(
    async (template: DateMenuTemplate) => {
      const newChefs: { [key: string]: string } = {}
      template.templates.forEach((menuTemplate) => {
        menuTemplate.chefIds?.forEach((chefId) => {
          if (!chefsMap.has(chefId)) {
            newChefs[chefId] = chefId
          }
        })

        // Also load excluded chef IDs
        menuTemplate.excludeChefIds?.forEach((chefId) => {
          if (!chefsMap.has(chefId)) {
            newChefs[chefId] = chefId
          }
        })
      })

      if (Object.keys(newChefs).length > 0) {
        const chefs = await loadChefs({ ids: Object.keys(newChefs).join(',') })
        chefs.forEach((chef) => {
          chefsMap.set(chef.id, chef)
        })
        setChefsMap(new Map(chefsMap))
      }
    },
    [loadChefs, chefsMap],
  )

  // Set template scope based on the current template
  useEffect(() => {
    if (templateHqId === hqId) {
      setTemplateScope('market')
    } else {
      setTemplateScope('global')
    }
  }, [templateHqId, hqId])

  useEffect(() => {
    if (!draftDateMenuTemplate) {
      return
    }
    onLoadDateMenuTemplateChefs(draftDateMenuTemplate)
  }, [draftDateMenuTemplate, onLoadDateMenuTemplateChefs])

  // Add effect to update draftDateMenuTemplate when initialTemplate changes
  useEffect(() => {
    setDraftDateMenuTemplate(initialTemplate)
    setActiveMenuTemplateIndex(0)
  }, [initialTemplate])

  // Add a new menu template to the current draftDateMenuTemplate
  const addMenuTemplate = () => {
    if (!draftDateMenuTemplate) {
      return
    }
    const updatedTemplates = [...draftDateMenuTemplate.templates]
    updatedTemplates.push({
      chefIds: [],
      excludeChefIds: [],
      cuisines: [],
      excludeCuisines: [],
      maxNumReuses: 1,
    })

    setDraftDateMenuTemplate({
      ...draftDateMenuTemplate,
      templates: updatedTemplates,
    })

    // Set the active template to the newly added one
    setActiveMenuTemplateIndex(updatedTemplates.length - 1)
  }

  // Delete a menu template from the draftDateMenuTemplate
  const deleteMenuTemplate = (menuIndex: number) => {
    if (!draftDateMenuTemplate) {
      return
    }
    const updatedTemplates = [...draftDateMenuTemplate.templates]
    updatedTemplates.splice(menuIndex, 1)

    setDraftDateMenuTemplate({
      ...draftDateMenuTemplate,
      templates: updatedTemplates,
    })

    // If we've deleted the active template, select an appropriate one
    if (activeMenuTemplateIndex >= updatedTemplates.length) {
      setActiveMenuTemplateIndex(Math.max(0, updatedTemplates.length - 1))
    }
  }

  // Update a template property
  const updateMenuTemplate = (
    menuIndex: number,
    field: keyof MenuTemplate,
    value: any,
  ) => {
    if (!draftDateMenuTemplate) {
      return
    }
    const updatedTemplates = [...draftDateMenuTemplate.templates]
    updatedTemplates[menuIndex] = {
      ...updatedTemplates[menuIndex],
      [field]: value,
    }

    setDraftDateMenuTemplate({
      ...draftDateMenuTemplate,
      templates: updatedTemplates,
    })
  }

  // Handle chef selection
  const handleChefSelect = (menuIndex: number, selectedChef: Chef) => {
    if (!draftDateMenuTemplate) {
      return
    }
    const selectedTemplate = draftDateMenuTemplate.templates[menuIndex]

    // Check if this chef is already in the excluded list
    if (selectedTemplate.excludeChefIds?.includes(selectedChef.id)) {
      displayFailureMessage(
        `Cannot add ${selectedChef.name} to both inclusion and exclusion lists`,
      )

      return
    }

    updateMenuTemplate(menuIndex, 'chefIds', [
      ...(selectedTemplate.chefIds || []),
      selectedChef.id,
    ])
    setChefsMap(new Map(chefsMap.set(selectedChef.id, selectedChef)))
  }

  const handleChefsSelect = (menuIndex: number, chefIds: string[]) => {
    if (!draftDateMenuTemplate) {
      return
    }
    const selectedTemplate = draftDateMenuTemplate.templates[menuIndex]

    // Check if this chef is already in the excluded list
    if (selectedTemplate.excludeChefIds?.some((id) => chefIds.includes(id))) {
      displayFailureMessage(
        'Cannot add overlapping chefs to both inclusion and exclusion lists',
      )

      return
    }

    updateMenuTemplate(menuIndex, 'chefIds', chefIds)
  }

  const handleRemoveChef = (menuIndex: number, chefId: string) => {
    if (!draftDateMenuTemplate) {
      return
    }
    const selectedTemplate = draftDateMenuTemplate.templates[menuIndex]
    let newChefIds =
      selectedTemplate.chefIds?.filter((id) => id !== chefId) || undefined

    if (newChefIds?.length === 0) {
      newChefIds = undefined
    }

    updateMenuTemplate(menuIndex, 'chefIds', newChefIds)
  }

  // Handle excluded chef selection
  const handleExcludeChefSelect = (menuIndex: number, selectedChef: Chef) => {
    if (!draftDateMenuTemplate) {
      return
    }
    const selectedTemplate = draftDateMenuTemplate.templates[menuIndex]

    // Check if this chef is already in the included list
    if (selectedTemplate.chefIds?.includes(selectedChef.id)) {
      displayFailureMessage(
        `Cannot add ${selectedChef.name} to both inclusion and exclusion lists`,
      )

      return
    }

    updateMenuTemplate(menuIndex, 'excludeChefIds', [
      ...(selectedTemplate.excludeChefIds || []),
      selectedChef.id,
    ])
    setChefsMap(new Map(chefsMap.set(selectedChef.id, selectedChef)))
  }

  const handleExcludeChefsSelect = (menuIndex: number, chefIds: string[]) => {
    if (!draftDateMenuTemplate) {
      return
    }
    const selectedTemplate = draftDateMenuTemplate.templates[menuIndex]

    // Check if any of the selected chefs are already in the inclusion list
    if (selectedTemplate.chefIds?.some((id) => chefIds.includes(id))) {
      displayFailureMessage(
        'Cannot add overlapping chefs to both inclusion and exclusion lists',
      )

      return
    }

    updateMenuTemplate(menuIndex, 'excludeChefIds', chefIds)
  }

  const handleRemoveExcludedChef = (menuIndex: number, chefId: string) => {
    if (!draftDateMenuTemplate) {
      return
    }
    const selectedTemplate = draftDateMenuTemplate.templates[menuIndex]
    let newExcludeChefIds =
      selectedTemplate.excludeChefIds?.filter((id) => id !== chefId) ||
      undefined

    if (newExcludeChefIds?.length === 0) {
      newExcludeChefIds = undefined
    }

    updateMenuTemplate(menuIndex, 'excludeChefIds', newExcludeChefIds)
  }

  // Handle cuisine selection
  const handleCuisineSelect = (menuIndex: number, selected: string[]) => {
    if (!draftDateMenuTemplate) {
      return
    }
    const selectedTemplate = draftDateMenuTemplate.templates[menuIndex]

    // Check for overlap with excluded cuisines
    if (selectedTemplate.excludeCuisines) {
      const overlap = selected.filter(
        (cuisine) => selectedTemplate.excludeCuisines?.includes(cuisine),
      )
      if (overlap.length > 0) {
        displayFailureMessage(
          `Cannot add ${overlap.join(
            ', ',
          )} to both inclusion and exclusion lists`,
        )

        return
      }
    }

    updateMenuTemplate(
      menuIndex,
      'cuisines',
      selected.length > 0 ? selected : undefined,
    )
  }

  // Handle excluded cuisine selection
  const handleExcludeCuisineSelect = (
    menuIndex: number,
    selected: string[],
  ) => {
    if (!draftDateMenuTemplate) {
      return
    }
    const selectedTemplate = draftDateMenuTemplate.templates[menuIndex]

    // Check for overlap with included cuisines
    if (selectedTemplate.cuisines) {
      const overlap = selected.filter(
        (cuisine) => selectedTemplate.cuisines?.includes(cuisine),
      )
      if (overlap.length > 0) {
        displayFailureMessage(
          `Cannot add ${overlap.join(
            ', ',
          )} to both inclusion and exclusion lists`,
        )

        return
      }
    }

    updateMenuTemplate(
      menuIndex,
      'excludeCuisines',
      selected.length > 0 ? selected : undefined,
    )
  }

  // Handle maximum reuses change
  const handleMaxReusesChange = (menuIndex: number, value: string) => {
    const maxReuses = value.trim() === '' ? 1 : Math.max(1, Number(value))
    updateMenuTemplate(menuIndex, 'maxNumReuses', maxReuses)
  }

  // Save template
  const handleSave = async () => {
    if (!draftDateMenuTemplate) {
      return
    }
    // Validate template before saving
    if (!draftDateMenuTemplate.name) {
      displayFailureMessage('Template name is required')

      return
    }

    if (draftDateMenuTemplate.templates.length === 0) {
      displayFailureMessage('At least one menu template is required')

      return
    }

    setIsSaving(true)

    try {
      let savedTemplate: DateMenuTemplate | null = null

      // Set the hqId based on the template scope
      const templateHqId = templateScope === 'global' ? null : hqId

      if (isNewTemplate) {
        // Create new template
        const createReq: CreateDateMenuTemplateReq = {
          createdByUserId: userId,
          createdByEmail: userEmail,
          hqId: templateHqId as number | undefined,
          isPrivate: draftDateMenuTemplate.isPrivate,
          name: draftDateMenuTemplate.name,
          templates: draftDateMenuTemplate.templates,
        }

        savedTemplate = await createDateMenuTemplate(createReq)

        if (savedTemplate) {
          displayInfoMessage(
            `Template "${savedTemplate.name}" created successfully`,
          )
          // Update the draft with the saved template info (including ID)
          setDraftDateMenuTemplate(savedTemplate)
          // Add to sidebar
          handleTemplateUpdate(savedTemplate)
        } else {
          displayFailureMessage('Failed to create template')
        }
      } else {
        // Update existing template
        const updateReq: UpdateDateMenuTemplateReq = {
          id: draftDateMenuTemplate.id,
          updatedByUserId: userId,
          updatedByEmail: userEmail,
          hqId: templateHqId as number | undefined,
          isPrivate: draftDateMenuTemplate.isPrivate,
          name: draftDateMenuTemplate.name,
          templates: draftDateMenuTemplate.templates,
        }

        savedTemplate = await updateDateMenuTemplate(updateReq)

        if (savedTemplate) {
          displayInfoMessage(
            `Template "${savedTemplate.name}" updated successfully`,
          )
          // Update the draft with the saved template info
          setDraftDateMenuTemplate(savedTemplate)
          // Pass to parent component
          handleTemplateUpdate(savedTemplate)
        } else {
          displayFailureMessage('Failed to update template')
        }
      }
    } catch (error) {
      displayFailureMessage(
        `An error occurred while saving the template: ${error}`,
      )
    } finally {
      setIsSaving(false)
    }
  }

  // Create new template
  const handleCreateNewTemplate = () => {
    setDraftDateMenuTemplate(buildDefaultTemplate(userId, userEmail))
    setActiveMenuTemplateIndex(0)
  }

  // Create copy template
  const handleCreateCopyTemplate = () => {
    if (!draftDateMenuTemplate) {
      return
    }
    setDraftDateMenuTemplate(
      copyTemplate(draftDateMenuTemplate, userId, userEmail),
    )
    setActiveMenuTemplateIndex(0)
  }

  // Handle exporting template
  const handleExportTemplate = () => {
    if (!draftDateMenuTemplate) {
      return
    }
    try {
      const templateJson = JSON.stringify(draftDateMenuTemplate, null, 2)
      navigator.clipboard
        .writeText(templateJson)
        .then(() =>
          displayInfoMessage('Date menu template copied to clipboard!'),
        )
    } catch (error) {
      displayFailureMessage('Error exporting template')
    }
  }

  // Handle importing template
  const handleImportTemplate = () => {
    try {
      setImportError(null)
      const importedTemplate = JSON.parse(importText) as DateMenuTemplate
      importedTemplate.id = ''

      // Validate the imported data
      if (!importedTemplate.name) {
        throw new Error('Imported template is missing a name')
      }

      if (!Array.isArray(importedTemplate.templates)) {
        throw new Error(
          `Template "${importedTemplate.name}" has invalid templates (not an array)`,
        )
      }

      setDraftDateMenuTemplate(importedTemplate)
      setImportText('')
      setShowImportExport(false)
      displayInfoMessage('Date menu template imported successfully!')
    } catch (error) {
      setImportError(
        `Import failed: ${
          error instanceof Error ? error.message : 'Invalid JSON format'
        }`,
      )
    }
  }

  return (
    <FlexContainer flexDirection="column" width="1000px">
      <FlexContainer
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        margin="0 0 20px 0"
        width="25%"
      >
        <Button
          label={
            showImportExport ? 'Hide Import/Export' : 'Import/Export Template'
          }
          onClick={() => setShowImportExport(!showImportExport)}
        />
      </FlexContainer>

      {showImportExport && (
        <ImportExportPanel>
          <FlexContainer flexDirection="column" width="100%">
            <ImportExportHeader>
              Export/Import Date Menu Template
            </ImportExportHeader>

            <ExportSection>
              <Button
                label="Export Template to Clipboard"
                onClick={handleExportTemplate}
                width="100%"
              />
              <HelperText>
                This will copy your template as JSON to your clipboard
              </HelperText>
            </ExportSection>

            <Divider />

            <ImportSection>
              <ImportExportHeader>Import Template</ImportExportHeader>
              <textarea
                placeholder="Paste template JSON here..."
                value={importText}
                onChange={(e) => {
                  setImportText(e.target.value)
                  if (importError) {
                    setImportError(null)
                  }
                }}
                style={{
                  width: '100%',
                  minHeight: '150px',
                  padding: '10px',
                  border: importError ? '1px solid red' : '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontFamily: 'monospace',
                  marginBottom: '10px',
                }}
              />
              {importError && <ErrorMessage>{importError}</ErrorMessage>}
              <Button
                label="Import Template"
                onClick={handleImportTemplate}
                disabled={!importText.trim()}
                width="100%"
              />
              <HelperText>
                Warning: This will replace your current template with the
                imported one
              </HelperText>
            </ImportSection>
          </FlexContainer>
        </ImportExportPanel>
      )}

      <MainContainer>
        <TemplateEditorPanel>
          <FlexContainer flexDirection="row" alignItems="center">
            <Button
              label={
                isNewTemplate ? 'Save New Template' : 'Save Updated Template'
              }
              onClick={handleSave}
              disabled={isSaving}
            />
            <XSpacing width="20px" />
            <Button label="New" onClick={handleCreateNewTemplate} />
            {draftDateMenuTemplate?.id && (
              <>
                <XSpacing width="20px" />
                <Button label="Copy" onClick={handleCreateCopyTemplate} />
              </>
            )}
            {draftDateMenuTemplate?.id && (
              <>
                <XSpacing width="20px" />
                <Button
                  label="Delete"
                  onClick={() => handleDeleteTemplate(draftDateMenuTemplate)}
                  backgroundColor={colors.violet}
                />
              </>
            )}
            {draftDateMenuTemplate?.id && onSelectTemplate && (
              <>
                <XSpacing width="20px" />
                <Button
                  label="Select"
                  onClick={() => onSelectTemplate(draftDateMenuTemplate)}
                  backgroundColor={colors.green300}
                />
              </>
            )}
          </FlexContainer>
          <YSpacing height="20px" />

          {draftDateMenuTemplate && (
            <>
              <TemplateEditorHeader>
                <Input
                  label="Template Name"
                  value={draftDateMenuTemplate.name}
                  onChange={(e) =>
                    setDraftDateMenuTemplate({
                      ...draftDateMenuTemplate,
                      name: e.target.value,
                    })
                  }
                />

                {/* Template scope and privacy options */}
                <TemplateConfigSection>
                  <ScopeContainer>
                    <SectionLabel>Template Scope</SectionLabel>
                    <RadioGroup
                      name="templateScope"
                      options={[
                        { value: 'global', label: 'Global' },
                        { value: 'market', label: 'Market' },
                      ]}
                      selectedValue={templateScope}
                      onChange={(value) =>
                        setTemplateScope(value as 'global' | 'market')
                      }
                    />
                    <HelperText>
                      Global templates are available across all markets. Market
                      templates are only available for this market.
                    </HelperText>
                  </ScopeContainer>

                  <PrivacyContainer>
                    <SectionLabel>Privacy</SectionLabel>
                    <Checkbox
                      label="Private template"
                      testId="is-private-checkbox"
                      value={draftDateMenuTemplate.isPrivate}
                      checked={draftDateMenuTemplate.isPrivate}
                      onChange={(e: any) =>
                        setDraftDateMenuTemplate({
                          ...draftDateMenuTemplate,
                          isPrivate: e.target.checked,
                        })
                      }
                    />
                    <HelperText>
                      Private templates are only visible to you. Non-private
                      templates are visible to all users.
                    </HelperText>
                  </PrivacyContainer>
                </TemplateConfigSection>
              </TemplateEditorHeader>

              {draftDateMenuTemplate.templates.length > 0 && (
                <>
                  <TabsContainer>
                    {draftDateMenuTemplate.templates.map((_, menuIndex) => (
                      <Tab
                        key={menuIndex}
                        onClick={() => setActiveMenuTemplateIndex(menuIndex)}
                        isActive={menuIndex === activeMenuTemplateIndex}
                      >
                        Menu {menuIndex + 1}
                      </Tab>
                    ))}
                    <AddTabButton onClick={addMenuTemplate}>
                      <FaPlus />
                    </AddTabButton>
                  </TabsContainer>

                  <TemplatesContainer>
                    {/* Render only the active template */}
                    <TemplatePanel width="100%" maxWidth="800px">
                      {activeMenuTemplateIndex <
                        draftDateMenuTemplate.templates.length && (
                        <>
                          <TemplateSection>
                            <SectionLabel>
                              Select Vendors (Optional)
                            </SectionLabel>
                            <AutocompleteInput
                              label="Select Chefs (Optional)"
                              loaderFunction={(search: any) =>
                                loadChefs({ ...search })
                              }
                              value=""
                              alreadySelectedIDs={
                                draftDateMenuTemplate.templates[
                                  activeMenuTemplateIndex
                                ].chefIds || []
                              }
                              onSelect={(chef: Chef) =>
                                handleChefSelect(activeMenuTemplateIndex, chef)
                              }
                            />
                            {dateMenuTemplateChefsOptions.length > 0 && (
                              <StyledSelect
                                value=""
                                onChange={(e) =>
                                  handleChefsSelect(
                                    activeMenuTemplateIndex,
                                    e.target.value.split(','),
                                  )
                                }
                              >
                                <option value="">
                                  Select Chef Group from Template
                                </option>
                                {dateMenuTemplateChefsOptions.map(
                                  (option, i) => (
                                    <option key={i} value={option.value}>
                                      {option.label}
                                    </option>
                                  ),
                                )}
                              </StyledSelect>
                            )}
                            <HelperText>
                              Matches any menu where its chef is in this list.
                              Leave empty to include all chefs.
                            </HelperText>
                            <FlexContainer width="80%" flexWrap="wrap">
                              {(
                                draftDateMenuTemplate.templates[
                                  activeMenuTemplateIndex
                                ].chefIds || []
                              ).map((chefId) => (
                                <SelectedOption
                                  key={chefId}
                                  onClick={() =>
                                    handleRemoveChef(
                                      activeMenuTemplateIndex,
                                      chefId,
                                    )
                                  }
                                >
                                  {chefsMap.get(chefId)?.name}{' '}
                                  <span style={{ color: 'red' }}>✕</span>
                                </SelectedOption>
                              ))}
                            </FlexContainer>
                          </TemplateSection>

                          <YSpacing height="20px" />

                          <TemplateSection>
                            <SectionLabel>
                              Exclude Vendors (Optional)
                            </SectionLabel>
                            <AutocompleteInput
                              label="Exclude Chefs (Optional)"
                              loaderFunction={(search: any) =>
                                loadChefs({ ...search })
                              }
                              value=""
                              alreadySelectedIDs={
                                draftDateMenuTemplate.templates[
                                  activeMenuTemplateIndex
                                ].excludeChefIds || []
                              }
                              onSelect={(chef: Chef) =>
                                handleExcludeChefSelect(
                                  activeMenuTemplateIndex,
                                  chef,
                                )
                              }
                            />
                            {dateMenuTemplateChefsOptions.length > 0 && (
                              <StyledSelect
                                value=""
                                onChange={(e) =>
                                  handleExcludeChefsSelect(
                                    activeMenuTemplateIndex,
                                    e.target.value.split(','),
                                  )
                                }
                              >
                                <option value="">
                                  Select Chef Group from Template
                                </option>
                                {dateMenuTemplateChefsOptions.map(
                                  (option, i) => (
                                    <option key={i} value={option.value}>
                                      {option.label}
                                    </option>
                                  ),
                                )}
                              </StyledSelect>
                            )}
                            <HelperText>
                              Matches any menu where its chef is NOT in this
                              list. Leave empty to include all chefs.
                            </HelperText>
                            <FlexContainer width="80%" flexWrap="wrap">
                              {(
                                draftDateMenuTemplate.templates[
                                  activeMenuTemplateIndex
                                ].excludeChefIds || []
                              ).map((chefId) => (
                                <SelectedOption
                                  key={chefId}
                                  onClick={() =>
                                    handleRemoveExcludedChef(
                                      activeMenuTemplateIndex,
                                      chefId,
                                    )
                                  }
                                >
                                  {chefsMap.get(chefId)?.name}{' '}
                                  <span style={{ color: 'red' }}>✕</span>
                                </SelectedOption>
                              ))}
                            </FlexContainer>
                          </TemplateSection>

                          <YSpacing height="20px" />

                          <TemplateSection>
                            <SectionLabel>
                              Select Cuisines (Optional)
                            </SectionLabel>
                            <MultiSelectDropDown
                              width="100%"
                              label=""
                              includeSelectAll={true}
                              options={cuisineOptions}
                              checkedOptions={
                                draftDateMenuTemplate.templates[
                                  activeMenuTemplateIndex
                                ].cuisines || []
                              }
                              renderCheckedOptions={true}
                              searchPlaceholder="Search cuisines..."
                              onChange={(selected: string[]) =>
                                handleCuisineSelect(
                                  activeMenuTemplateIndex,
                                  selected,
                                )
                              }
                            />
                            <HelperText>
                              Matches any menu where any of its cuisines is in
                              this list. Leave empty to include all cuisines.
                            </HelperText>
                          </TemplateSection>

                          <YSpacing height="20px" />

                          <TemplateSection>
                            <SectionLabel>
                              Exclude Cuisines (Optional)
                            </SectionLabel>
                            <MultiSelectDropDown
                              width="100%"
                              label=""
                              includeSelectAll={true}
                              options={cuisineOptions}
                              checkedOptions={
                                draftDateMenuTemplate.templates[
                                  activeMenuTemplateIndex
                                ].excludeCuisines || []
                              }
                              renderCheckedOptions={true}
                              searchPlaceholder="Search cuisines to exclude..."
                              onChange={(selected: string[]) =>
                                handleExcludeCuisineSelect(
                                  activeMenuTemplateIndex,
                                  selected,
                                )
                              }
                            />
                            <HelperText>
                              Matches any menu where none of its cuisines is in
                              this list. Leave empty to include all cuisines.
                            </HelperText>
                          </TemplateSection>

                          <YSpacing height="20px" />

                          <TemplateSection>
                            <SectionLabel>
                              Maximum Number of Reuses
                            </SectionLabel>
                            <DaysInput
                              type="number"
                              min="1"
                              value={
                                draftDateMenuTemplate.templates[
                                  activeMenuTemplateIndex
                                ].maxNumReuses
                              }
                              onChange={(e) =>
                                handleMaxReusesChange(
                                  activeMenuTemplateIndex,
                                  e.target.value,
                                )
                              }
                              placeholder="Enter number"
                            />
                            <HelperText>
                              The maximum number of times a menu can be reused
                              (minimum 1, where 1 means no reuse)
                            </HelperText>
                          </TemplateSection>

                          <YSpacing height="20px" />

                          {draftDateMenuTemplate.templates.length > 1 && (
                            <FlexContainer
                              justifyContent="flex-end"
                              width="100%"
                            >
                              <DeleteTemplateButton
                                onClick={() =>
                                  deleteMenuTemplate(activeMenuTemplateIndex)
                                }
                              >
                                <FaTrash />
                                Delete This Menu Template
                              </DeleteTemplateButton>
                            </FlexContainer>
                          )}
                        </>
                      )}
                    </TemplatePanel>
                  </TemplatesContainer>
                </>
              )}
            </>
          )}

          <YSpacing height="20px" />
        </TemplateEditorPanel>
      </MainContainer>
    </FlexContainer>
  )
}

// Styled components
const MainContainer = styled.div`
  display: flex;
  width: 100%;
`

const TemplateEditorPanel = styled.div`
  flex: 1;
  border: 1px solid ${colors.gray200};
  border-radius: 8px;
  padding: 15px;
  width: 100%;
`

const TemplateEditorHeader = styled.div`
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid ${colors.gray200};
`

const TemplatesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`

const TemplatePanel = styled(Panel)`
  padding: 20px;
  border: 1px solid ${colors.gray200};
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`

const TemplateSection = styled.div`
  margin-bottom: 10px;
`

const SectionLabel = styled.label`
  display: block;
  font-weight: bold;
  margin-bottom: 8px;
`

const DaysInput = styled.input`
  width: 100%;
  padding: 8px;
  border: 1px solid ${colors.gray300};
  border-radius: 4px;
  font-size: 14px;
`

const HelperText = styled.div`
  font-size: 12px;
  color: ${colors.gray400};
  margin-top: 5px;
  font-style: italic;
`

const SelectedOption = styled.button`
  border: 1px solid #ccc;
  border-radius: 10px;
  padding: 4px 8px;
  margin-top: 4px;
  margin-right: 4px;
  margin-bottom: 4px;
`

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${colors.gray200};
  margin: 15px 0;
  width: 100%;
`

const ImportExportPanel = styled.div`
  background-color: white;
  border: 1px solid ${colors.gray200};
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  width: 100%;
`

const ExportSection = styled.div`
  margin-bottom: 15px;
`

const ImportSection = styled.div`
  margin-top: 15px;
`

const ImportExportHeader = styled.h3`
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 10px;
`

const ErrorMessage = styled.div`
  color: red;
  font-size: 13px;
  margin-bottom: 10px;
  padding: 5px 10px;
  background-color: rgba(255, 0, 0, 0.05);
  border-radius: 4px;
  border-left: 3px solid red;
`

const TabsContainer = styled.div`
  display: flex;
  gap: 2px;
  margin-bottom: 20px;
  border-bottom: 1px solid ${colors.gray200};
  overflow-x: auto;
  padding-bottom: 0;
`

const Tab = styled.button<{ isActive: boolean }>`
  background: ${(props) => (props.isActive ? colors.blue100 : colors.gray100)};
  border: 1px solid
    ${(props) => (props.isActive ? colors.blue300 : colors.gray200)};
  border-bottom: ${(props) =>
    props.isActive ? 'none' : `1px solid ${colors.gray200}`};
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  color: ${(props) => (props.isActive ? colors.blue400 : colors.gray400)};
  cursor: pointer;
  font-size: 14px;
  font-weight: ${(props) => (props.isActive ? 'bold' : 'normal')};
  padding: 8px 16px;
  position: relative;
  bottom: -1px;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background-color: ${(props) =>
      props.isActive ? colors.blue100 : colors.gray200};
  }
`

const AddTabButton = styled.button`
  background: ${colors.gray100};
  border: 1px dashed ${colors.blue300};
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  color: ${colors.blue400};
  cursor: pointer;
  font-size: 14px;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: #e3f2fd;
  }
`

const DeleteTemplateButton = styled.button`
  background-color: #fff0f0;
  border: 1px solid #ffd7d7;
  border-radius: 4px;
  color: #d32f2f;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 15px;
  transition: all 0.2s ease;

  &:hover {
    background-color: #ffe5e5;
    border-color: #ffbdbd;
    color: #c62828;
  }
`

const StyledSelect = styled.select`
  width: 100%;
  padding: 8px;
  border: 1px solid ${colors.gray300};
  border-radius: 4px;
  font-size: 14px;
  margin-top: 8px;
  margin-bottom: 8px;
  background-color: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${colors.blue300};
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }

  &:hover {
    border-color: ${colors.blue300};
  }
`

const TemplateConfigSection = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 15px;
`

const ScopeContainer = styled.div`
  flex: 1;
`

const PrivacyContainer = styled.div`
  flex: 1;
`

export default MenuTemplateBuilder
